import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ShieldAlert, Check } from "lucide-react";
import beraStaysLogo from "@/assets/berastays-logo.png";
import beraStaysDesktopLogo from "@/assets/logo-desktop.png";
import { useAuth } from "@/contexts/AuthContext";
import MobileUserDrawer from "@/components/layout/MobileUserDrawer";
import DesktopUserPanel from "@/components/layout/DesktopUserPanel";
import NotificationBell from "@/components/notifications/NotificationBell";
import { LanguageSelector } from "@/components/ui/LanguageSelector";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useUserProfile } from "@/hooks/useUserProfile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

// Custom SVG icons for nav — distinctive stroke style
const IconExplore = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" opacity="0.15" stroke="none" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

const IconHeart = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor" opacity="0.1" stroke="none" />
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const IconOffer = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" fill="currentColor" opacity="0.1" stroke="none" />
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <circle cx="7" cy="7" r="1.5" fill="currentColor" opacity="0.4" />
  </svg>
);


const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const hideMobileLangSelector = location.pathname === '/' || location.pathname === '/explorar' || location.pathname.startsWith('/dashboard');

  // Listen for bottom tab menu trigger
  useEffect(() => {
    const handler = () => setIsMenuOpen(true);
    window.addEventListener('open-mobile-menu', handler);
    return () => window.removeEventListener('open-mobile-menu', handler);
  }, []);
  const [isDesktopPanelOpen, setIsDesktopPanelOpen] = useState(false);
  const { user, signOut, loading } = useAuth();
  const { isHost, isGuest, isSuperAdmin } = useUserRoles();
  const navigate = useNavigate();

  // Single shared profile cache to prevent verified-status flapping.
  const { profile, isVerified, hasSubmittedVerification, needsVerification } = useUserProfile();

  // Get avatar URL with fallback chain
  const getAvatarUrl = () => {
    if (profile?.avatar_url) return profile.avatar_url;
    return null;
  };

  const isHostOnly = isHost && !isGuest && !isSuperAdmin;

  const navLinks: { label: string; href: string; icon: React.ComponentType<{ className?: string }> }[] = [];

  // Explorar for guests
  if (!user || isGuest || isSuperAdmin) {
    navLinks.push({ label: t('nav.explore'), href: "/explorar", icon: IconExplore });
  }

  // Favoritos only in header for non-host-only users
  if (!isHostOnly) {
    navLinks.push({ label: t('nav.favorites'), href: "/favoritos", icon: IconHeart });
  }

  // Fazer Oferta: only for guests and super admin (not hosts)
  if (!user || isGuest || isSuperAdmin) {
    navLinks.push({ label: t('nav.makeOffer'), href: "/ofertas/nova", icon: IconOffer });
  }

  const handleSignOut = async () => {
    await signOut();
    // Navigate to landing page with fromLogout state to allow access via PublicOnlyRoute
    navigate("/", { replace: true, state: { fromLogout: true } });
  };

  const getInitials = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.slice(0, 2).toUpperCase() || "U";
  };

  const isMobileOrTablet = typeof window !== 'undefined' && window.innerWidth < 1024;
  const [isMobileView, setIsMobileView] = useState(isMobileOrTablet);

  React.useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setIsMobileView(mql.matches);
    mql.addEventListener('change', onChange);
    setIsMobileView(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);  // Mobile/Tablet: floating menu
  if (isMobileView) {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 z-50 bg-background flex items-center justify-between px-4 pt-[max(env(safe-area-inset-top),10px)] pb-2 border-b border-gray-100/50">
          {/* Header Title / Auth Buttons */}
          <Link to="/" className="flex items-center group shrink-0">
            <img 
              src={beraStaysLogo} 
              alt="BeraStays" 
              className="h-9 w-auto max-w-[140px] object-contain group-hover:scale-105 transition-transform"
            />
          </Link>
          {!user && (
            <div className="flex items-center gap-1 ml-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/auth')}
                className="text-gray-700 hover:text-[#E8513D] hover:bg-[#E8513D]/5 px-2 text-xs h-8"
              >
                Entrar
              </Button>
              <Button
                size="sm"
                onClick={() => navigate('/auth?tab=register')}
                className="bg-[#E8513D] hover:bg-[#D14430] text-white px-2.5 text-xs h-8"
              >
                Registar
              </Button>
            </div>
          )}
          
          <div className="flex-1" />

          {/* Action Buttons: Notification + Language + Hamburger */}
          <div className="flex items-center gap-4">
            {!hideMobileLangSelector && <LanguageSelector variant="mobile" />}
            {user && (
              <motion.div
                initial={{ opacity: 0, filter: "blur(6px)" }}
                animate={{ opacity: 1, filter: "blur(0px)" }}
                transition={{ duration: 0.2, delay: 0.3 }}
              >
                <NotificationBell />
              </motion.div>
            )}
            
            <motion.button
              onClick={() => setIsMenuOpen(true)}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-white to-gray-50/90 dark:bg-none dark:bg-[#1C1C1E] backdrop-blur-xl border border-[#E8513D]/30 dark:border-transparent shadow-md dark:shadow-none flex items-center justify-center active:scale-90 transition-all hover:border-[#E8513D]/50 dark:hover:border-[#3A3A3A] hover:shadow-lg hover:shadow-[#E8513D]/10"
              initial={{ opacity: 0, filter: "blur(8px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.2, delay: 0.2 }}
              whileTap={{ scale: 0.85 }}
              whileHover={{ scale: 1.05 }}
              aria-label="Abrir menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E8513D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </motion.button>
          </div>
        </div>
        <MobileUserDrawer
          open={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          user={user}
          loading={loading}
          getInitials={getInitials}
          onSignOut={handleSignOut}
        />
      </>
    );
  }

  // Desktop: standard header bar
  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
      {/* BeraStays coral accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
      <div className="container mx-auto px-4">
        <div className="flex items-center h-16 md:h-20">
          {/* Logo - Left (flex-1 for equal distribution) */}
          <div className="flex-1 flex items-center justify-start">
            <Link to="/" className="flex items-center group">
              <img 
                src={beraStaysDesktopLogo} 
                alt="BeraStays" 
                className="h-[52px] md:h-[60px] w-auto max-w-[380px] object-contain group-hover:scale-105 transition-transform"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Center (flex-1 with justify-center) */}
          <nav className="flex-1 flex items-center justify-center gap-1">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.href}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Link
                  to={user ? link.href : "/auth"}
                  className="group relative flex items-center gap-3 px-4 py-2 rounded-2xl text-foreground/70 hover:text-foreground bg-card/60 hover:bg-card border border-border/40 hover:border-primary/30 transition-all duration-300 shadow-sm hover:shadow-lg hover:shadow-primary/5"
                >
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent flex items-center justify-center group-hover:from-primary/20 group-hover:via-primary/10 ring-1 ring-primary/10 group-hover:ring-primary/25 transition-all duration-300">
                    <link.icon className="w-[16px] h-[16px] text-primary group-hover:scale-110 transition-transform" />
                  </div>
                  <span className="font-semibold text-[13px] tracking-tight">{link.label}</span>
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Right Section: Auth Buttons + Language Selector */}
          <div className="flex-1 flex items-center justify-end gap-4">
            <LanguageSelector variant="desktop" />
            
            {/* Desktop Auth Buttons */}
            <div className="flex items-center gap-5">
              {loading ? (
                <div className="w-11 h-11 rounded-full bg-muted animate-pulse" />
              ) : user ? (
                <>
                  <NotificationBell />
                  <motion.button
                    onClick={() => setIsDesktopPanelOpen(true)}
                    className="relative group"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="relative">
                      {/* Unverified pulse effect */}
                      {needsVerification && (
                        <div className="absolute -inset-2 rounded-full bg-primary/30 animate-ping" />
                      )}
                      <div className={`absolute -inset-1 rounded-full bg-gradient-to-r from-primary/50 via-bera-gold/50 to-primary/50 opacity-0 group-hover:opacity-100 blur transition-opacity duration-300 ${needsVerification ? 'animate-pulse opacity-70' : ''}`} />
                      <Avatar className={`relative h-11 w-11 border-2 shadow-lg ring-2 ring-background ${needsVerification ? 'border-primary animate-pulse' : 'border-primary/20'}`}>
                        {getAvatarUrl() && (
                          <AvatarImage 
                            src={getAvatarUrl()!} 
                            alt="Avatar"
                            className="object-cover"
                          />
                        )}
                        <AvatarFallback className={`font-semibold text-sm ${needsVerification ? 'gradient-primary text-white' : 'gradient-primary text-white'}`}>
                          {getInitials()}
                        </AvatarFallback>
                      </Avatar>
                      {/* Status indicator */}
                      {needsVerification ? (
                        <motion.span 
                          className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-primary rounded-full ring-2 ring-background"
                          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                        />
                      ) : (

                        <motion.span 
                          className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full ring-2 ring-background"
                          animate={{ scale: [1, 1.15, 1] }}
                          transition={{ repeat: Infinity, duration: 2 }}
                        />
                      )}
                    </div>
                  </motion.button>
                  <DesktopUserPanel
                    open={isDesktopPanelOpen}
                    onOpenChange={setIsDesktopPanelOpen}
                    user={user}
                    getInitials={getInitials}
                    getAvatarUrl={getAvatarUrl}
                    onSignOut={handleSignOut}
                  />
                </>
              ) : (
                <motion.div 
                  className="flex items-center gap-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <Button 
                    variant="ghost" 
                    className="font-medium hover:bg-muted/60 transition-all h-10"
                    asChild
                  >
                    <Link to="/auth">{t('nav.login')}</Link>
                  </Button>
                  <Button 
                    className="gradient-primary text-white font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 h-10 px-5 rounded-xl" 
                    asChild
                  >
                    <Link to="/auth?mode=register">
                      {t('nav.register')}
                    </Link>
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
